import type { CustomerConfigState, FeatureState } from '../../shared/types/customer';
import { toCustomerConfigState } from '../../shared/utils/tenant-config';
import pagesRegistry from '../assets/json/pages.json';

interface PageDefinition {
	title: string;
	path: string;
	icon: string;
	order: number;
}

type PagesRegistry = Record<string, PageDefinition>;

/**
 * The app's window onto the active tenant.
 *
 * A thin projection over the registry package's `useTenant()`: that composable
 * owns resolution (dev cookie, build pin, runtime key, host, default key) and
 * holds the entry in its own `useState`, so there is no fetch, no cache key and
 * no hydration gap to manage here. Everything below is a read of that entry.
 */
export function useCustomerConfig() {
	const tenant = useTenant();

	const config = computed<CustomerConfigState | null>(() =>
		tenant.entry.value ? toCustomerConfigState(tenant.entry.value) : null,
	);

	const error = computed(() => (tenant.error.value ? new Error(tenant.error.value) : null));

	/**
	 * Kept for the three call sites that awaited config before routing. The
	 * registry resolves synchronously, so this only re-runs resolution (which is
	 * what the DevTenantSwitcher path wants) and hands back the current value.
	 */
	async function fetchConfig(): Promise<CustomerConfigState | null> {
		if (!tenant.entry.value) tenant.resolve();
		return config.value;
	}

	function isFeatureEnabled(featureId: string): boolean {
		return config.value?.features[featureId]?.enabled ?? false;
	}

	function isComponentEnabled(featureId: string, componentId: string): boolean {
		if (!isFeatureEnabled(featureId)) return false;
		return config.value?.features[featureId]?.components[componentId] ?? false;
	}

	function getFeatureConfig<T = unknown>(featureId: string, configKey: string): T | undefined {
		return config.value?.features[featureId]?.config[configKey] as T | undefined;
	}

	function getFeatureState(featureId: string): FeatureState | undefined {
		return config.value?.features[featureId];
	}

	function isPageEnabled(pageId: string): boolean {
		// Page gating is `pages.json` enablement only; the registry's
		// `requiredFeatures` cross-check never ran (registry always empty).
		return config.value?.pages[pageId]?.enabled ?? false;
	}

	const homePage = computed(() => config.value?.homePage);

	const homePagePath = computed(() => {
		if (!homePage.value) return undefined;
		const registry = pagesRegistry as PagesRegistry;
		return registry[homePage.value]?.path;
	});

	const enabledPages = computed(() => {
		if (!config.value?.pages) return [];
		const registry = pagesRegistry as PagesRegistry;
		const homeId = homePage.value;
		return Object.entries(config.value.pages)
			.filter(([_, pageState]) => pageState.enabled)
			.map(([pageId, pageState]) => ({
				id: pageId,
				...registry[pageId],
				config: pageState.config,
				// Home page always sorts first
				...(homeId === pageId ? { order: -1 } : {}),
			}))
			.sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
	});

	const productType = computed(() => config.value?.productType);
	const isLimousine = computed(() => productType.value === 'limousine');
	const isTow = computed(() => productType.value === 'towing');

	const portalType = computed(() => config.value?.portalType);
	const isMapping = computed(() => portalType.value === 'mapping');
	const isSupplier = computed(() => portalType.value === 'supplier');

	const branding = computed(() => config.value?.branding);

	return {
		config,
		error,

		fetchConfig,

		isFeatureEnabled,
		isComponentEnabled,
		getFeatureConfig,
		getFeatureState,

		isPageEnabled,
		enabledPages: readonly(enabledPages),

		productType,
		isLimousine,
		isTow,

		portalType,
		isMapping,
		isSupplier,
		branding,

		homePage,
		homePagePath,
	};
}

export type UseCustomerConfig = ReturnType<typeof useCustomerConfig>;
